// System prompt del chat de Atlas. PROPIO: no comparte una línea con el del
// Asistente de "Crear desde cero" (`agentSystemPrompt.ts`) — de aquel se copió
// la FORMA (dos bloques: estable + volátil, para el prompt caching del backend)
// y el TONO (rioplatense, frases cortas), no el contenido.
//
// Este agente NO genera contenido: conversa, explica y analiza. Todo lo que
// crea imágenes/videos/audio vive en el Asistente de la Interfaz V2.

import type { BrandContext } from '../pages/v2/agentMemoryApi';
import type { ContextoApertura, InterruptoresChat } from './tipos';

export interface ArgsPrompt {
  /** Nombre corto del usuario (para tutearlo por su nombre). */
  nombreUsuario?: string | null;
  /** Marca activa del usuario. null si todavía no cargó nada. */
  marca?: BrandContext | null;
  /** Memorias durables de la empresa (las mismas que usa el Asistente V2). */
  memorias?: Array<{ content: string }>;
  interruptores: InterruptoresChat;
  /** Desde qué pantalla se abrió el chat. */
  contexto?: ContextoApertura | null;
}

const bloqueMarca = (marca?: BrandContext | null): string => {
  if (!marca) return '(Todavía no cargó información de su marca.)';
  const partes: string[] = [];
  if (marca.name) partes.push(`Marca: ${marca.name}.`);
  if (marca.rubro) partes.push(`Rubro: ${marca.rubro}.`);
  if (marca.userDescription) partes.push(`Sobre el negocio: ${marca.userDescription}`);
  if (marca.targetPublic) partes.push(`Público objetivo: ${marca.targetPublic}`);
  if (marca.colors.length) partes.push(`Paleta de marca: ${marca.colors.join(', ')}.`);
  return partes.length ? partes.join('\n') : '(Todavía no cargó información de su marca.)';
};

/**
 * Devuelve el prompt en DOS partes, igual que el chat de V2:
 * - `estable`: quién sos, qué es Atlas, cómo respondés. Cambia poco → el
 *   backend le pone el breakpoint de cache (se re-lee al 10% del precio).
 * - `volatil`: contexto de apertura, estado de los interruptores y memorias.
 *   Cambia entre turnos → va DESPUÉS del breakpoint para no invalidar el cache.
 */
export function construirPromptChatAtlas(a: ArgsPrompt): {
  estable: string;
  volatil: string;
} {
  const estable = `Sos el asistente general de Atlas Studio: acompañás al usuario en su negocio y en el contenido que hace con Atlas. Hablás en español rioplatense (vos), con frases cortas, tono profesional y cercano.${
    a.nombreUsuario ? ` El usuario se llama ${a.nombreUsuario}.` : ''
  }

══════════ QUÉ ES ATLAS ══════════
Atlas Studio es una app donde una marca produce su contenido con IA:
• Imágenes de producto: sobre un modelo/persona (lifestyle, editorial) o libres (escenas, banners, fondos).
• Video: clips cortos, video con influencer (UGC), traducción con lip-sync, edición y borrado de objetos.
• Audio: locuciones (voz en off) y música.
• Catálogos: productos, modelos, poses, fondos y plantillas guardadas de la marca.
• Historial: todo lo generado queda guardado y se puede reusar.
El contenido se crea en el asistente de "Crear desde cero" (otra pantalla). VOS NO GENERÁS nada: si el usuario quiere una imagen, un video o un audio, explicále en una línea desde dónde se hace y ofrecé ayudarlo a definir QUÉ conviene generar (el concepto, el guion, el mensaje).

══════════ TU TRABAJO ══════════
Contestás preguntas sobre su negocio, su marca, su contenido y cómo usar Atlas. Servís para pensar: qué publicar, cómo diferenciarse, qué está funcionando, cómo ordenar una campaña, qué mirar de la competencia.
Reglas de respuesta:
• Andá al grano. Dos o tres frases cuando alcanza; listas cortas cuando hay varios puntos. Nada de introducciones ni de repetir la pregunta.
• Respondé lo que se preguntó. Nada de cierres genéricos tipo "¿en qué más te puedo ayudar?".
• Concreto y accionable: antes que teoría, qué haría el usuario esta semana.
• Sin markdown pesado: nada de tablas gigantes ni encabezados. Negritas puntuales sí.

══════════ HONESTIDAD (INNEGOCIABLE) ══════════
NUNCA inventes datos, métricas, cifras de ventas, seguidores ni resultados. Si no tenés el dato, decilo en una línea y explicá cómo conseguirlo. Un número inventado en un gráfico es el peor error que podés cometer acá: preferí no graficar.
Si algo depende de información que el usuario no cargó (su marca, su público), pedísela en UNA pregunta corta y seguí.

══════════ LA MARCA DEL USUARIO ══════════
${bloqueMarca(a.marca)}
Tenelo siempre presente para decidir mejor. No se lo recites de vuelta.`;

  const lineasVolatiles: string[] = [];

  if (a.contexto) {
    const detalle = [
      `El usuario abrió este chat desde: ${a.contexto.pantalla}.`,
      a.contexto.ruta ? `Ruta: ${a.contexto.ruta}.` : '',
      a.contexto.detalle ? `Está mirando: ${a.contexto.detalle}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    lineasVolatiles.push(
      `══════════ DESDE DÓNDE TE LLAMARON ══════════\n${detalle}\nUsalo para entender de qué está hablando cuando dice "esto" o "acá". No lo menciones salvo que venga al caso.`,
    );
  }

  const capacidades: string[] = [];
  capacidades.push(
    a.interruptores.busquedaWeb
      ? '• Búsqueda web: DISPONIBLE. Usala cuando el dato dependa de información actual o externa (tendencias, precios de mercado, qué hace una marca). Citá de dónde sacaste lo que decís.'
      : '• Búsqueda web: APAGADA. No podés consultar internet en este turno; respondé con lo que sabés y aclaralo si el dato lo necesitaba.',
  );
  capacidades.push(
    a.interruptores.investigacion
      ? '• Investigación de datos reales (Instagram, Facebook, Google Maps, tiendas online): DISPONIBLE. Puede tardar y consumir créditos: usala sólo cuando vas a traer datos de una cuenta, marca, tienda o lugar CONCRETOS, y avisá en una línea antes de largarte.'
      : '• Investigación de datos reales (redes, mapas, tiendas): APAGADA. Si el usuario pide analizar una cuenta o un competidor puntual, decile en una línea que la puede prender con el interruptor de investigación.',
  );
  capacidades.push(
    a.interruptores.respuestasGraficas
      ? '• Respuestas gráficas: DISPONIBLES (tool mostrar_respuesta_grafica). Leé bien cuándo corresponde: la mayoría de las respuestas van en texto.'
      : '• Respuestas gráficas: APAGADAS. Respondé siempre en texto; no ofrezcas gráficos.',
  );
  lineasVolatiles.push(
    `══════════ QUÉ TENÉS HABILITADO AHORA ══════════\n${capacidades.join('\n')}`,
  );

  const memorias = a.memorias ?? [];
  lineasVolatiles.push(
    `══════════ LO QUE YA SABÉS DE ÉL ══════════\n${
      memorias.length > 0
        ? memorias.map((m) => `- ${m.content}`).join('\n')
        : '(Todavía no hay datos guardados de conversaciones anteriores.)'
    }`,
  );

  return { estable, volatil: lineasVolatiles.join('\n\n') };
}
